/* Zoom image produit ==================================================================================================== */

((document) => {
  document.addEventListener("click", (event) => {
    const clickTarget = event.target.closest("[data-lightbox-click]");

    if (!clickTarget) return;

    const lightboxButton = clickTarget.querySelector(
      'button[is="media-lightbox-button"], button[is="media-hover-button"]',
    );

    if (!lightboxButton) return;

    lightboxButton.click();
  });
})(document);

/* Variantes produit ==================================================================================================== */

(() => {
  const gridSelector = ".swatches--variant.swatches--grid";
  /** @type {WeakSet<Element>} */
  const managedRoots = new WeakSet();
  const fontReady = document.fonts?.ready || Promise.resolve();

  const toNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getGridGap = (grid) => {
    const styles = getComputedStyle(grid);
    return toNumber(styles.columnGap || styles.gap);
  };

  const getTextWidth = (() => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    return (label) => {
      if (!context) return 0;

      const styles = getComputedStyle(label);
      const text = label.textContent?.trim() || "";
      if (!text) return 0;

      context.font = [
        styles.fontStyle,
        styles.fontVariant,
        styles.fontWeight,
        styles.fontStretch,
        `${styles.fontSize}/${styles.lineHeight}`,
        styles.fontFamily,
      ]
        .filter(Boolean)
        .join(" ");

      const measurement = context.measureText(text).width;
      const letterSpacing = toNumber(styles.letterSpacing);
      const padding =
        toNumber(styles.paddingLeft) + toNumber(styles.paddingRight);
      const borders =
        toNumber(styles.borderLeftWidth) + toNumber(styles.borderRightWidth);

      return (
        measurement +
        padding +
        borders +
        letterSpacing * Math.max(text.length - 1, 0)
      );
    };
  })();

  const getLongestLabelWidth = (grid) => {
    const labels = Array.from(grid.querySelectorAll(".label-swatch"));
    if (!labels.length) return 0;

    return labels.reduce(
      (width, label) => Math.max(width, getTextWidth(label)),
      0,
    );
  };

  const getItemCount = (grid) =>
    Array.from(grid.children).filter((child) => child.matches("li")).length;

  /** Conteneur commun pour aligner les largeurs entre toutes les lignes d’options */
  const getSwatchGridSyncRoot = (grid) =>
    grid.closest("variant-picker") ||
    grid.closest("product-info") ||
    grid.closest("[data-bundle-variant-picker]") ||
    grid;

  /** @type {Set<Element>} */
  const pendingSyncRoots = new Set();
  let syncRootsScheduled = false;

  const scheduleSyncSwatchGridsInRoot = (root) => {
    if (!root) return;
    pendingSyncRoots.add(root);
    if (syncRootsScheduled) return;
    syncRootsScheduled = true;
    queueMicrotask(() => {
      syncRootsScheduled = false;
      pendingSyncRoots.forEach((r) => {
        syncSwatchGridsInRoot(r);
      });
      pendingSyncRoots.clear();
    });
  };

  /**
   * Largeur minimale commune = celle imposée par la ligne d’options dont le libellé le plus long est le plus large.
   * Chaque grille garde le calcul habituel : colonnes à partir de ce minimum, puis largeur réelle = max(min, remplissage).
   */
  const syncSwatchGridsInRoot = (root) => {
    const grids = Array.from(root.querySelectorAll(gridSelector));
    if (!grids.length) return;

    let sharedMin = 0;
    for (const grid of grids) {
      sharedMin = Math.max(sharedMin, Math.ceil(getLongestLabelWidth(grid)));
    }

    const maxColumns = 6;

    for (const grid of grids) {
      const availableWidth = grid.clientWidth;
      const gap = getGridGap(grid);
      const items = getItemCount(grid);

      if (!availableWidth || !items) continue;

      if (!sharedMin) {
        const longestWidth = Math.ceil(getLongestLabelWidth(grid));
        if (!longestWidth) continue;
        const columns = Math.max(
          1,
          Math.min(
            maxColumns,
            items,
            Math.floor((availableWidth + gap) / (longestWidth + gap)),
          ),
        );
        const distributedWidth = Math.max(
          longestWidth,
          (availableWidth - gap * Math.max(columns - 1, 0)) / columns,
        );
        grid.style.setProperty(
          "--custom-swatch-item-width",
          `${distributedWidth}px`,
        );
        grid.style.setProperty("--custom-swatch-columns", columns);
        grid.style.setProperty("--custom-swatch-justify", "center");
        continue;
      }

      const columns = Math.max(
        1,
        Math.min(
          maxColumns,
          items,
          Math.floor((availableWidth + gap) / (sharedMin + gap)),
        ),
      );
      const distributedWidth = Math.max(
        sharedMin,
        (availableWidth - gap * Math.max(columns - 1, 0)) / columns,
      );

      grid.style.setProperty(
        "--custom-swatch-item-width",
        `${distributedWidth}px`,
      );
      grid.style.setProperty("--custom-swatch-columns", columns);
      grid.style.setProperty("--custom-swatch-justify", "center");
    }
  };

  const observeGrid = (grid) => {
    const root = getSwatchGridSyncRoot(grid);
    if (!managedRoots.has(root)) {
      const resizeObserver = new ResizeObserver(() =>
        scheduleSyncSwatchGridsInRoot(root),
      );
      resizeObserver.observe(root);
      managedRoots.add(root);
    }

    scheduleSyncSwatchGridsInRoot(root);
    fontReady.then(() => scheduleSyncSwatchGridsInRoot(root));
  };

  const initGrids = (scope = document) => {
    scope.querySelectorAll(gridSelector).forEach(observeGrid);
  };

  document.addEventListener("DOMContentLoaded", () => {
    initGrids();
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches(gridSelector)) {
            observeGrid(node);
            return;
          }

          initGrids(node);
        });
      });
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });
  });

  document.addEventListener("shopify:section:load", (event) => {
    initGrids(event.target);
  });
})();

/* Bundle variant selectors ============================================================================= */

(() => {
  const selectors = {
    bundleDetails: 'details[is="product-bundle-details"]',
    checkbox: 'input[name="bundles"][data-product-id]',
    template: "template[data-bundle-variant-template]",
    slot: "[data-bundle-variant-slot]",
    picker: "[data-bundle-variant-picker]",
    heading: "[data-bundle-variant-heading]",
    label: "[data-bundle-variant-label]",
    variantSelect: "[data-bundle-variant-select]",
    price: "[data-bundle-price]",
    item: "[data-bundle-item]",
    info: "[data-bundle-info]",
  };

  const updateCheckbox = (details, productId, value, price) => {
    const checkbox = details.querySelector(
      `${selectors.checkbox}[data-product-id="${productId}"]`,
    );
    if (!checkbox) return;

    if (value) {
      checkbox.value = value;
    }

    if (price !== undefined && price !== null && `${price}` !== "") {
      checkbox.dataset.price = price;
      checkbox.setAttribute("data-price", price);
    }
  };

  const parseJson = (value) => {
    try {
      return JSON.parse(value);
    } catch (error) {
      return [];
    }
  };

  const toPrice = (value) => {
    if (value === undefined || value === null || value === "") return null;

    const price = Number(value);
    return Number.isFinite(price) ? price : null;
  };

  const formatMoney = (price) => {
    const format = theme?.settings?.currencyCodeEnabled
      ? theme?.settings?.moneyWithCurrencyFormat
      : theme?.settings?.moneyFormat;

    if (theme?.Currency?.formatMoney && format) {
      return theme.Currency.formatMoney(price, format);
    }

    const numeric = Number(price);
    if (!Number.isFinite(numeric)) return "";

    const amount = numeric / 100;
    return amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const renderBadge = (priceElement, price, comparePrice) => {
    const badgeEnabled = priceElement.dataset.badgeEnabled === "true";
    if (!badgeEnabled || !comparePrice || comparePrice <= price) return "";

    if (price === 0 && priceElement.dataset.badgeFree) {
      return `<span class="badge${
        priceElement.classList.contains("badge--vertical")
          ? " badge--vertical"
          : ""
      } badge--onsale flex items-center gap-1d5 font-medium leading-none rounded-full">${
        priceElement.dataset.badgeFree
      }</span>`;
    }

    const saveType = priceElement.dataset.badgeSaveType || "percent";
    const saleLabel = priceElement.dataset.badgeSaleLabel || "";
    const saveTemplate = priceElement.dataset.badgeSaveTemplate || "";

    let label = "";

    if (saveType === "label" && saleLabel) {
      label = saleLabel;
    } else if (saveType === "dollar") {
      const saved = comparePrice - price;
      const formattedSaved = formatMoney(saved);
      if (formattedSaved) {
        label = saveTemplate.replace("__amount__", formattedSaved);
      }
    } else {
      const savedPercent = Math.round(
        ((comparePrice - price) * 100) / comparePrice,
      );
      label = saveTemplate.replace("__amount__", `${savedPercent}%`);
    }

    if (!label) return "";

    return `<span class="badge${
      priceElement.classList.contains("badge--vertical")
        ? " badge--vertical"
        : ""
    } badge--onsale flex items-center gap-1d5 font-medium leading-none rounded-full">${label}</span>`;
  };

  // Helper function to get variant compare_at_price from product variants data
  const getVariantCompareAtPrice = (
    variantId,
    productId,
    searchContext = null,
  ) => {
    if (!variantId) return null;

    // If a search context is provided (e.g., modal), use it first
    let variantDataScript = null;

    if (searchContext) {
      // Try to find variant data script in the provided context
      variantDataScript = searchContext.querySelector(
        `script[data-product-variant-data]`,
      );

      // If not found, try to find product-info in the context
      if (!variantDataScript) {
        const productInfo = searchContext.querySelector(
          `product-info[data-product-id="${productId}"]`,
        );
        if (productInfo) {
          variantDataScript = productInfo.querySelector(
            "script[data-product-variant-data]",
          );
        }
      }
    }

    // If still not found, try to find product info element globally
    if (!variantDataScript) {
      const productInfo = document.querySelector(
        `product-info[data-product-id="${productId}"], product-card-info[data-product-id="${productId}"]`,
      );
      if (productInfo) {
        variantDataScript = productInfo.querySelector(
          "script[data-product-variant-data]",
        );
      }
    }

    if (!variantDataScript) return null;

    try {
      const variantData = JSON.parse(variantDataScript.textContent);
      if (variantData?.productVariants) {
        const variant = variantData.productVariants.find(
          (v) => v.id === variantId || String(v.id) === String(variantId),
        );
        if (variant?.compare_at_price) {
          return Number(variant.compare_at_price);
        }
      }
    } catch (error) {
      // Ignore JSON parse errors
    }

    return null;
  };

  const renderPriceMarkup = (priceElement, price, comparePrice) => {
    const formatted = formatMoney(price);
    if (!formatted) return "";

    const regular = `<span class="price__regular whitespace-nowrap">${formatted}</span>`;

    if (comparePrice && comparePrice > price) {
      const compareFormatted = formatMoney(comparePrice) || "";
      const badge = renderBadge(priceElement, price, comparePrice);
      const saleLabel = priceElement.dataset.salePriceLabel || "";
      const regularLabel = priceElement.dataset.regularPriceLabel || "";

      return [
        `<span class="sr-only">${saleLabel}</span>`,
        regular,
        `<span class="sr-only">${regularLabel}</span>`,
        `<span class="price__sale inline-flex items-center h-auto relative">${compareFormatted}</span>`,
        badge,
      ].join("");
    }

    return regular;
  };

  const updatePriceDisplays = (details, productId, price, comparePrice) => {
    details
      .querySelectorAll(`${selectors.price}[data-product-id="${productId}"]`)
      .forEach((priceElement) => {
        const priceValue =
          price !== undefined && price !== null
            ? price
            : toPrice(priceElement.dataset.currentPrice);

        // Si comparePrice est explicitement passé (même si null), l'utiliser
        // Sinon, utiliser le fallback depuis les attributs data
        let compareValueRaw = null;
        if (comparePrice !== undefined) {
          // comparePrice a été explicitement passé (peut être null)
          compareValueRaw =
            comparePrice !== null ? toPrice(comparePrice) : null;
        } else {
          // comparePrice n'a pas été passé, utiliser le fallback
          compareValueRaw = toPrice(
            priceElement.dataset.comparePrice ||
              priceElement.dataset.currentComparePrice,
          );
        }

        const compareValue =
          compareValueRaw !== null &&
          priceValue !== null &&
          compareValueRaw > priceValue
            ? compareValueRaw
            : null;

        const markup = renderPriceMarkup(
          priceElement,
          priceValue,
          compareValue,
        );
        if (markup) {
          priceElement.innerHTML = markup;
          priceElement.dataset.currentPrice = priceValue;
          if (compareValue && compareValue > priceValue) {
            priceElement.dataset.currentComparePrice = compareValue;
            priceElement.classList.add("price--on-sale");
          } else {
            priceElement.dataset.currentComparePrice = "";
            priceElement.classList.remove("price--on-sale");
          }
        }
      });
  };

  const resetPriceDisplays = (details, productId) => {
    // Get the checkbox to find the default variant ID (for unchecked products, this is the first available variant)
    const checkbox = details.querySelector(
      `${selectors.checkbox}[data-product-id="${productId}"]`,
    );
    const defaultVariantId = checkbox?.value;
    const isChecked = checkbox?.checked === true;

    details
      .querySelectorAll(`${selectors.price}[data-product-id="${productId}"]`)
      .forEach((priceElement) => {
        const min = toPrice(priceElement.dataset.priceMin);
        const max = toPrice(priceElement.dataset.priceMax);
        const fromText = priceElement.dataset.priceFrom;

        // Calculate compareValue (for both checked and unchecked products)
        let compareValue = toPrice(priceElement.dataset.comparePrice);

        // Also check variant's native compare_at_price (for the default variant)
        // This handles cases where the variant has a discount independent of bundle discount
        if (defaultVariantId) {
          // Find the search context (modal or parent container)
          let searchContext = details;
          if (!searchContext) {
            // Try to find modal context
            searchContext = priceElement.closest(
              '.quick-view, .x-modal, [class*="modal"]',
            );
          }

          const variantCompareAtPrice = getVariantCompareAtPrice(
            defaultVariantId,
            productId,
            searchContext,
          );
          // Use the maximum compare price (bundle discount or variant native discount)
          if (variantCompareAtPrice !== null && variantCompareAtPrice > min) {
            if (compareValue === null || variantCompareAtPrice > compareValue) {
              compareValue = variantCompareAtPrice;
            }
          }
        }

        const hasSavings =
          compareValue !== null && min !== null && compareValue > min;
        const hasVariablePrices = max !== null && min !== null && max > min;

        // Si le produit est décoché
        if (!isChecked) {
          // Si les prix varient (max > min), on affiche "Dès €..." sans réduction
          // même si une variante a une réduction
          if (hasVariablePrices && fromText) {
            priceElement.classList.remove("price--on-sale");
            priceElement.dataset.currentComparePrice = "";
            const upToDiscount = priceElement.dataset.upToDiscount;
            priceElement.innerHTML = upToDiscount
              ? `<span class="price__regular price__regular--from whitespace-nowrap">${fromText}</span><span class="price__discount-range whitespace-nowrap">${upToDiscount}</span>`
              : `<span class="price__regular price__regular--from whitespace-nowrap">${fromText}</span>`;
            priceElement.dataset.currentPrice = "";
            return;
          }

          // Si les prix sont fixes (max === min) ET qu'il y a une réduction, on l'affiche
          if (hasSavings && !hasVariablePrices) {
            const markup = renderPriceMarkup(priceElement, min, compareValue);
            if (markup) {
              priceElement.innerHTML = markup;
              priceElement.dataset.currentPrice = min;
              priceElement.dataset.currentComparePrice = compareValue;
              priceElement.classList.add("price--on-sale");
            }
            return;
          }

          // Sinon, afficher le prix minimum sans réduction
          priceElement.classList.remove("price--on-sale");
          priceElement.dataset.currentComparePrice = "";
          if (min !== null) {
            const formatted = formatMoney(min);
            if (formatted) {
              priceElement.innerHTML = `<span class="price__regular whitespace-nowrap">${formatted}</span>`;
              priceElement.dataset.currentPrice = min;
            }
          }
          return;
        }

        // Si le produit est coché, on peut afficher les réductions normalement
        if (hasSavings) {
          const markup = renderPriceMarkup(priceElement, min, compareValue);
          if (markup) {
            priceElement.innerHTML = markup;
            priceElement.dataset.currentPrice = min;
            priceElement.dataset.currentComparePrice = compareValue;
            priceElement.classList.add("price--on-sale");
          }
          return;
        }

        priceElement.classList.remove("price--on-sale");
        priceElement.dataset.currentComparePrice = "";

        if (hasVariablePrices && fromText) {
          priceElement.innerHTML = `<span class="price__regular price__regular--from whitespace-nowrap">${fromText}</span>`;
          priceElement.dataset.currentPrice = "";
          return;
        }

        if (min !== null) {
          const formatted = formatMoney(min);
          if (formatted) {
            priceElement.innerHTML = `<span class="price__regular whitespace-nowrap">${formatted}</span>`;
            priceElement.dataset.currentPrice = min;
          }
        }
      });
  };

  const normalizePriceDisplay = (priceElement) => {
    const basePrice = toPrice(priceElement.dataset.priceMin);
    if (basePrice === null) return;

    // Get the product ID from the price element
    const productId = priceElement.dataset.productId;
    const max = toPrice(priceElement.dataset.priceMax);
    const fromText = priceElement.dataset.priceFrom;

    // Check if prices vary
    const hasVariablePrices =
      max !== null && basePrice !== null && max > basePrice;

    // Start with comparePrice from dataset
    let comparePrice = toPrice(priceElement.dataset.comparePrice);

    // Also check variant's native compare_at_price (for the default variant)
    // This handles cases where the variant has a discount independent of bundle discount
    if (productId) {
      // Try to find the checkbox to get the default variant ID
      const priceElementParent = priceElement.closest(
        'details[is="product-bundle-details"]',
      );

      // Find the search context (modal or parent container)
      let searchContext = priceElementParent;
      if (!searchContext) {
        // Try to find modal context
        searchContext = priceElement.closest(
          '.quick-view, .x-modal, [class*="modal"]',
        );
      }

      if (priceElementParent) {
        const checkbox = priceElementParent.querySelector(
          `${selectors.checkbox}[data-product-id="${productId}"]`,
        );
        const defaultVariantId = checkbox?.value;
        const isChecked = checkbox?.checked === true;

        if (defaultVariantId) {
          // Pass the search context to getVariantCompareAtPrice
          const variantCompareAtPrice = getVariantCompareAtPrice(
            defaultVariantId,
            productId,
            searchContext,
          );
          // Use the maximum compare price (bundle discount or variant native discount)
          if (
            variantCompareAtPrice !== null &&
            variantCompareAtPrice > basePrice
          ) {
            if (comparePrice === null || variantCompareAtPrice > comparePrice) {
              comparePrice = variantCompareAtPrice;
            }
          }
        }

        // Si le produit n'est pas coché et que les prix varient, afficher "Dès €..."
        if (!isChecked && hasVariablePrices && fromText) {
          priceElement.classList.remove("price--on-sale");
          priceElement.dataset.currentComparePrice = "";
          const upToDiscount = priceElement.dataset.upToDiscount;
          priceElement.innerHTML = upToDiscount
            ? `<span class="price__regular price__regular--from whitespace-nowrap">${fromText}</span><span class="price__discount-range whitespace-nowrap">${upToDiscount}</span>`
            : `<span class="price__regular price__regular--from whitespace-nowrap">${fromText}</span>`;
          priceElement.dataset.currentPrice = "";
          return;
        }

        // Si le produit n'est pas coché, les prix sont fixes ET qu'il y a une réduction, on l'affiche
        if (!isChecked && !hasVariablePrices) {
          const hasSavings = comparePrice !== null && comparePrice > basePrice;
          if (hasSavings) {
            const markup = renderPriceMarkup(
              priceElement,
              basePrice,
              comparePrice,
            );
            if (markup) {
              priceElement.innerHTML = markup;
              priceElement.dataset.currentPrice = basePrice;
              priceElement.dataset.currentComparePrice = comparePrice;
              priceElement.classList.add("price--on-sale");
            }
            return;
          } else {
            // Pas de réduction, afficher le prix minimum
            const formatted = formatMoney(basePrice);
            if (formatted) {
              priceElement.innerHTML = `<span class="price__regular whitespace-nowrap">${formatted}</span>`;
              priceElement.dataset.currentPrice = basePrice;
              priceElement.dataset.currentComparePrice = "";
              priceElement.classList.remove("price--on-sale");
            }
            return;
          }
        }
      }
    }

    // Pour les produits cochés ou si on n'a pas trouvé la checkbox, normaliser normalement
    const hasSavings = comparePrice !== null && comparePrice > basePrice;

    // Si les prix varient et qu'on a le texte "Dès €...", l'afficher
    if (hasVariablePrices && fromText) {
      priceElement.classList.remove("price--on-sale");
      priceElement.dataset.currentComparePrice = "";
      priceElement.innerHTML = `<span class="price__regular price__regular--from whitespace-nowrap">${fromText}</span>`;
      priceElement.dataset.currentPrice = "";
      return;
    }

    const markup = renderPriceMarkup(
      priceElement,
      basePrice,
      hasSavings ? comparePrice : null,
    );
    if (!markup) return;

    priceElement.innerHTML = markup;
    priceElement.dataset.currentPrice = basePrice;
    priceElement.dataset.currentComparePrice = hasSavings ? comparePrice : "";
    priceElement.classList.toggle("price--on-sale", hasSavings);
  };

  const animateSlotVisibility = async (slot, show) => {
    const content = slot.firstElementChild;
    const cleanup = () => {
      if (show) {
        slot.style.height = "auto";
        slot.style.overflow = "visible";
        slot.style.margin = "";
        slot.style.removeProperty("padding-inline");
      } else {
        slot.style.height = "0";
        slot.style.overflow = "hidden";
        slot.style.margin = "0";
        slot.style.paddingInline = "0";
        // padding-bottom is managed by animatePaddingBottom
      }
      slot.style.removeProperty("transition");
      content?.style.removeProperty("opacity");
      content?.style.removeProperty("transform");
    };

    if (!content) {
      slot.style.height = show ? "auto" : "0";
      slot.style.overflow = show ? "visible" : "hidden";
      slot.style.margin = show ? "" : "0";
      if (show) {
        slot.style.removeProperty("padding-inline");
      } else {
        slot.style.paddingInline = "0";
      }
      return;
    }

    // S'assurer que le slot est visible pour mesurer la hauteur
    if (slot.hidden) {
      slot.hidden = false;
    }
    slot.style.overflow = "hidden";

    // When opening, measure the natural CSS padding-bottom (e.g. padding-block-end: var(--sp-4))
    // so we can include it in endHeight — otherwise it's clipped by overflow:hidden during animation.
    let naturalPaddingBottom = 0;
    if (show) {
      slot.style.removeProperty("padding-bottom");
      naturalPaddingBottom = parseFloat(getComputedStyle(slot).paddingBottom) || 0;
      slot.style.paddingBottom = "0";
    }

    const startHeight = show ? 0 : slot.clientHeight || content.clientHeight;
    const endHeight = show ? (content.scrollHeight || content.clientHeight) + naturalPaddingBottom : 0;

    if (startHeight === endHeight) {
      cleanup();
      return;
    }

    slot.style.height = `${startHeight}px`;
    content.style.opacity = show ? "0" : "1";
    content.style.transform = show ? "translateY(10px)" : "translateY(0)";

    const runMotion = async () => {
      await Motion.timeline(
        show
          ? [
              [
                slot,
                { height: [`${startHeight}px`, `${endHeight}px`] },
                { duration: 0.25, easing: "ease" },
              ],
              [
                content,
                {
                  opacity: [0, 1],
                  transform: ["translateY(10px)", "translateY(0)"],
                },
                { duration: 0.15, at: "-0.1" },
              ],
            ]
          : [
              [content, { opacity: 0 }, { duration: 0.15 }],
              [
                slot,
                { height: [`${startHeight}px`, `${endHeight}px`] },
                { duration: 0.25, at: "<", easing: "ease" },
              ],
            ],
      ).finished;
    };

    const runFallback = async () => {
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      const existingTransition = slot.style.transition;
      const heightTransition = `height var(--animation-primary) ease, opacity var(--animation-primary) ease, transform var(--animation-primary) ease`;
      slot.style.transition = existingTransition
        ? `${existingTransition}, ${heightTransition}`
        : heightTransition;
      slot.style.height = `${endHeight}px`;
      content.style.opacity = show ? "1" : "0";
      content.style.transform = "translateY(0)";

      await new Promise((resolve) => {
        const done = (event) => {
          if (event.target === slot && event.propertyName === "height") {
            slot.removeEventListener("transitionend", done);
            resolve();
          }
        };
        slot.addEventListener("transitionend", done);
      });
    };

    try {
      if (window.Motion?.timeline) {
        await runMotion();
      } else {
        await runFallback();
      }
    } finally {
      cleanup();
    }
  };

  // Returns true if item is the last [data-bundle-item] among its siblings.
  const isLastItem = (item) => {
    if (!item?.parentElement) return false;
    const all = item.parentElement.querySelectorAll(`:scope > ${selectors.item}`);
    return all[all.length - 1] === item;
  };

  // Animate padding-bottom of el in parallel with the slot height animation.
  // activate=true (open): 0 → natural CSS value.
  // activate=false (close): natural CSS value → 0.
  const animatePaddingBottom = (el, activate) => {
    if (!el) return Promise.resolve();

    if (!el.dataset.naturalPb) {
      const prev = el.style.paddingBottom;
      el.style.removeProperty("padding-bottom");
      el.dataset.naturalPb = getComputedStyle(el).paddingBottom || "0px";
      el.style.paddingBottom = prev; // restore even if "0" (prev="" removes the property)
    }

    const natural = el.dataset.naturalPb;
    if (natural === "0px" || natural === "0") return Promise.resolve();

    const from = activate ? "0px" : natural;
    const to = activate ? natural : "0px";
    const finalize = () =>
      activate
        ? el.style.removeProperty("padding-bottom")
        : (el.style.paddingBottom = "0");

    if (from === to) { finalize(); return Promise.resolve(); }

    if (window.Motion?.timeline) {
      return window.Motion.timeline([
        [el, { paddingBottom: [from, to] }, { duration: 0.25, easing: "ease" }],
      ]).finished.then(finalize);
    }

    return new Promise((resolve) => {
      const cleanup = () => {
        clearTimeout(timer);
        el.removeEventListener("transitionend", onEnd);
        el.style.removeProperty("transition");
        finalize();
        resolve();
      };
      const onEnd = (e) =>
        e.target === el && e.propertyName === "padding-bottom" && cleanup();
      const timer = setTimeout(cleanup, 400);
      el.addEventListener("transitionend", onEnd);
      el.style.transition = "padding-bottom var(--animation-primary) ease";
      el.style.paddingBottom = from;
      requestAnimationFrame(() => { el.style.paddingBottom = to; });
    });
  };

  const setSlotActive = async (
    details,
    productId,
    active,
    onHidden,
    immediate = false,
  ) => {
    const slot = details.querySelector(
      `${selectors.slot}[data-product-id="${productId}"]`,
    );

    if (!slot) return;

    if (slot.hidden) slot.hidden = false;

    const item = slot.closest(selectors.item);
    // Animate the slot's own padding-bottom for all items.
    // For last items, the slot has no CSS padding-block-end, so this is a no-op.
    const paddingEl = slot;

    // Last item : on contrôle aussi le padding-bottom inline de info ici.
    // - Slot ouvert  → on libère l'override pour que le padding CSS (var(--sp-4))
    //                  s'applique entre le label et le picker.
    // - Slot fermé   → on garde l'override à 0 pour conserver la structure visuelle
    //                  d'origine du dernier item.
    const syncLastItemInfoPadding = (isOpen) => {
      if (!isLastItem(item)) return;
      const info = item?.querySelector(selectors.info);
      if (!info) return;
      if (isOpen) {
        info.style.removeProperty("padding-bottom");
      } else {
        info.style.paddingBottom = "0";
      }
    };

    const isActive = slot.dataset.active === "true";

    if (isActive === active && !immediate) {
      if (!active) {
        slot.style.height = "0";
        slot.style.overflow = "hidden";
        slot.style.margin = "0";
        slot.style.paddingInline = "0";
        if (paddingEl) paddingEl.style.paddingBottom = "0";
        syncLastItemInfoPadding(false);
        onHidden?.();
      } else {
        // Déjà ouvert : on s'assure que info ait bien son padding CSS restauré
        // (cas où init avait forcé padding-bottom: 0 sur le last item).
        syncLastItemInfoPadding(true);
      }
      return;
    }

    slot.dataset.active = active ? "true" : "false";

    if (immediate) {
      if (active) {
        slot.style.height = "auto";
        slot.style.overflow = "visible";
        slot.style.margin = "";
        slot.style.removeProperty("padding-inline");
        if (paddingEl) paddingEl.style.removeProperty("padding-bottom");
        syncLastItemInfoPadding(true);
      } else {
        slot.style.height = "0";
        slot.style.overflow = "hidden";
        slot.style.margin = "0";
        slot.style.paddingInline = "0";
        if (paddingEl) paddingEl.style.paddingBottom = "0";
        syncLastItemInfoPadding(false);
      }
      if (!active) onHidden?.();
    } else {
      if (active) {
        // Remove inline padding-inline so CSS padding-inline applies immediately
        slot.style.removeProperty("padding-inline");
        // Libère le padding-bottom de info AVANT l'animation pour que le picker
        // ait son espace dès la première frame.
        syncLastItemInfoPadding(true);
      }

      await Promise.all([
        animateSlotVisibility(slot, active),
        animatePaddingBottom(paddingEl, active),
      ]);

      if (!active) {
        slot.style.margin = "0";
        slot.style.paddingInline = "0";
        syncLastItemInfoPadding(false);
      } else {
        slot.style.margin = "";
      }
      if (!active) onHidden?.();
    }
  };

  const formatSelection = (optionNames, optionValues) => {
    if (!Array.isArray(optionValues)) return "";

    const names = Array.isArray(optionNames) ? optionNames : [];
    const pairs = optionValues
      .map((value, index) => {
        const name = names[index];
        if (name) return `${name}: ${value}`;
        return value || "";
      })
      .filter(Boolean);

    return pairs.join(" · ");
  };

  const bindPicker = (details, picker) => {
    const selector = picker.querySelector(selectors.variantSelect);
    if (!selector) return;

    const productId = picker.dataset.productId;
    const heading = picker.querySelector(selectors.heading);
    const label = picker.querySelector(selectors.label);
    const optionNames = parseJson(picker.dataset.optionNames || "[]");

    const applySelection = () => {
      const selectedOption =
        selector.tagName === "SELECT" ? selector.selectedOptions[0] : selector;
      const selectedPrice = toPrice(selectedOption?.dataset.price);
      let comparePrice = toPrice(selectedOption?.dataset.comparePrice);

      // Also check variant's native compare_at_price (if available)
      // This handles cases where variant has a discount independent of bundle discount
      const variantId = selector.value;
      const variantCompareAtPrice = getVariantCompareAtPrice(
        variantId,
        productId,
      );

      // Use the maximum compare price (bundle discount or variant native discount)
      // But only if it's actually greater than the selected price (real discount)
      if (
        variantCompareAtPrice !== null &&
        variantCompareAtPrice > selectedPrice
      ) {
        // If we have both, use the maximum
        if (comparePrice === null || variantCompareAtPrice > comparePrice) {
          comparePrice = variantCompareAtPrice;
        }
      }

      // IMPORTANT: Ensure comparePrice is null if it's not greater than selectedPrice
      // This ensures badges and strikethrough prices are removed when switching to variants without discounts
      if (comparePrice !== null && comparePrice <= selectedPrice) {
        comparePrice = null;
      }

      updateCheckbox(details, productId, selector.value, selectedPrice);

      const optionValues = parseJson(
        selectedOption?.dataset.optionValues || "[]",
      );
      const summary =
        formatSelection(optionNames, optionValues) ||
        selectedOption?.textContent?.trim() ||
        "";

      if (heading) heading.textContent = summary;
      if (label) label.textContent = summary;
      if (selectedPrice !== null) {
        // Always pass comparePrice explicitly (can be null) to ensure proper cleanup
        updatePriceDisplays(details, productId, selectedPrice, comparePrice);
      }

      details.dispatchEvent(new Event("change", { bubbles: true }));
    };

    // Éviter d'ajouter plusieurs fois le même event listener
    if (picker.dataset.bound !== "true") {
      selector.addEventListener("change", applySelection);
      picker.dataset.bound = "true";
    }

    // Toujours appeler applySelection pour mettre à jour le prix,
    // même si le picker est déjà bound (important lors des réinitialisations)
    applySelection();
  };

  const renderPicker = (details, productId) => {
    const slot = details.querySelector(
      `${selectors.slot}[data-product-id="${productId}"]`,
    );
    if (!slot) return null;

    // Vérifier d'abord si un picker existe déjà dans le slot
    const existing = slot.querySelector(
      `${selectors.picker}[data-product-id="${productId}"]`,
    );
    if (existing) {
      bindPicker(details, existing);
      setSlotActive(details, productId, true);
      return existing;
    }

    // Si aucun picker n'existe, chercher le template pour le cloner
    const template = details.querySelector(
      `${selectors.template}[data-product-id="${productId}"]`,
    );
    if (!template) return null;

    slot.appendChild(template.content.cloneNode(true));
    const picker = slot.querySelector(
      `${selectors.picker}[data-product-id="${productId}"]:last-of-type`,
    );
    if (picker) {
      bindPicker(details, picker);
      setSlotActive(details, productId, true);
    }
    return picker;
  };

  const removePicker = (details, productId) => {
    const slot = details.querySelector(
      `${selectors.slot}[data-product-id="${productId}"]`,
    );
    if (!slot) return;

    // Masquer le slot avec animation sans supprimer le contenu du DOM
    setSlotActive(details, productId, false);
  };

  const syncCheckboxState = (details, checkbox, immediate = false) => {
    const productId = checkbox.dataset.productId;

    if (checkbox.checked) {
      renderPicker(details, productId);
      if (immediate) {
        const slot = details.querySelector(
          `${selectors.slot}[data-product-id="${productId}"]`,
        );
        if (slot) {
          setSlotActive(details, productId, true, null, true);
        }
      }
    } else {
      removePicker(details, productId);
      resetPriceDisplays(details, productId);
      const basePrice = toPrice(checkbox.dataset.priceMin);
      if (basePrice !== null) {
        updateCheckbox(details, productId, checkbox.value, basePrice);
      }
      if (immediate) {
        setSlotActive(details, productId, false, null, true);
      }
    }
  };

  const initBundleDetails = (details, preserveState = false) => {
    const slots = details.querySelectorAll(selectors.slot);
    if (!slots.length) return;

    const checkboxes = details.querySelectorAll(selectors.checkbox);
    let hasChecked = false;

    // Ne pas réattacher l'événement si déjà attaché
    if (!details.dataset.bundleInitialized) {
      details.addEventListener("change", (event) => {
        const checkbox = event.target.closest(selectors.checkbox);
        if (!checkbox) return;

        syncCheckboxState(details, checkbox);
      });
      details.dataset.bundleInitialized = "true";
    }

    // ÉTAPE 1 : Fermer IMMÉDIATEMENT tous les slots non cochés et supprimer leurs badges
    // Cela doit être fait AVANT toute autre opération pour éviter le flash visuel
    checkboxes.forEach((checkbox) => {
      const productId = checkbox.dataset.productId;
      if (!productId) return;

      if (!checkbox.checked) {
        // Fermer le slot IMMÉDIATEMENT
        const slot = details.querySelector(
          `${selectors.slot}[data-product-id="${productId}"]`,
        );
        if (slot) {
          // Retirer hidden si présent pour permettre les styles
          if (slot.hidden) {
            slot.hidden = false;
          }
          // Forcer la fermeture immédiate sans animation
          slot.dataset.active = "false";
          slot.style.height = "0";
          slot.style.overflow = "hidden";
          slot.style.margin = "0";
          slot.style.paddingInline = "0";
          slot.style.paddingBottom = "0";
          const closedItem = slot.closest(selectors.item);
          if (isLastItem(closedItem)) {
            const info = closedItem?.querySelector(selectors.info);
            if (info) info.style.paddingBottom = "0";
          }
          // Forcer un reflow pour s'assurer que les styles sont appliqués
          void slot.offsetHeight;
        }

        // Réinitialiser les prix pour les produits non cochés
        // resetPriceDisplays gère correctement les réductions (affiche le badge si comparePrice > priceMin)
        // et les prix variables (affiche "Dès €..." si max > min)
        resetPriceDisplays(details, productId);
      }
    });

    // ÉTAPE 2 : Initialiser l'état de chaque slot selon l'état de la checkbox
    // Maintenant que les slots non cochés sont fermés, on peut initialiser correctement
    checkboxes.forEach((checkbox) => {
      const productId = checkbox.dataset.productId;
      const slot = details.querySelector(
        `${selectors.slot}[data-product-id="${productId}"]`,
      );

      if (slot) {
        // Retirer hidden si présent
        if (slot.hidden) {
          slot.hidden = false;
        }

        // Vérifier si le slot est déjà dans le bon état
        // Cela évite les réinitialisations inutiles qui pourraient ouvrir les slots
        const isActive = slot.dataset.active === "true";
        const shouldBeActive = checkbox.checked;

        // Si l'état correspond déjà et qu'on préserve l'état, juste rebind le picker si nécessaire
        // MAIS au premier chargement (preserveState = false), toujours forcer l'initialisation
        if (preserveState && isActive === shouldBeActive) {
          if (shouldBeActive) {
            const picker = slot.querySelector(
              `${selectors.picker}[data-product-id="${productId}"]`,
            );
            if (picker) {
              bindPicker(details, picker);
            }
          }
          // Ne pas modifier l'état du slot, il est déjà correct
          if (checkbox.checked) {
            hasChecked = true;
          }
          return;
        }

        // Si l'état ne correspond pas, forcer la réinitialisation selon la checkbox
        // Cela garantit que les slots restent fermés si la checkbox n'est pas cochée

        if (checkbox.checked) {
          const openItem = slot.closest(selectors.item);
          slot.dataset.active = "true";
          slot.style.height = "auto";
          slot.style.overflow = "visible";
          slot.style.margin = "";
          slot.style.removeProperty("padding-inline");
          slot.style.removeProperty("padding-bottom");
          // Last item: restaurer le padding-bottom CSS sur info pour que le picker
          // ne soit pas collé au label quand le slot est ouvert.
          if (isLastItem(openItem)) {
            const info = openItem?.querySelector(selectors.info);
            if (info) info.style.removeProperty("padding-bottom");
          }
          const picker = slot.querySelector(
            `${selectors.picker}[data-product-id="${productId}"]`,
          );
          if (picker) {
            bindPicker(details, picker);
          }
        } else {
          // S'assurer que le slot reste fermé (même s'il a été fermé à l'étape 1)
          const closeItem = slot.closest(selectors.item);
          slot.dataset.active = "false";
          slot.style.height = "0";
          slot.style.overflow = "hidden";
          slot.style.margin = "0";
          slot.style.paddingInline = "0";
          slot.style.paddingBottom = "0";
          if (isLastItem(closeItem)) {
            const info = closeItem?.querySelector(selectors.info);
            if (info) info.style.paddingBottom = "0";
          }
        }
      }

      if (checkbox.checked) {
        hasChecked = true;
        // S'assurer que le picker est bien lié si la checkbox est cochée
        const picker = slot?.querySelector(
          `${selectors.picker}[data-product-id="${productId}"]`,
        );
        if (picker) {
          bindPicker(details, picker);
        }
      }
      // Note: La suppression des badges pour les produits non cochés est déjà faite à l'étape 1
    });

    // Normaliser les prix APRÈS avoir initialisé les slots
    // Si on préserve l'état, ne pas normaliser (ils ont déjà été restaurés)
    if (!preserveState) {
      // Normaliser les prix pour TOUS les produits (cochés et non cochés)
      // Cela garantit que les prix barrés et badges apparaissent immédiatement
      checkboxes.forEach((checkbox) => {
        const productId = checkbox.dataset.productId;
        if (!productId) return;

        if (checkbox.checked) {
          // Pour les produits cochés, normaliser seulement si le picker n'est pas encore lié
          const picker = details.querySelector(
            `${selectors.picker}[data-product-id="${productId}"]`,
          );

          // Seulement normaliser si le picker n'existe pas ou n'est pas encore lié
          // Si le picker est lié, bindPicker a déjà géré le prix via updatePriceDisplays
          if (!picker || picker.dataset.bound !== "true") {
            details
              .querySelectorAll(
                `${selectors.price}[data-product-id="${productId}"]`,
              )
              .forEach((priceElement) => {
                normalizePriceDisplay(priceElement);
              });
          }
        } else {
          // Pour les produits non cochés, normaliser aussi les prix
          // Cela garantit que les prix barrés et badges apparaissent immédiatement
          // même si le produit n'est pas coché
          details
            .querySelectorAll(
              `${selectors.price}[data-product-id="${productId}"]`,
            )
            .forEach((priceElement) => {
              normalizePriceDisplay(priceElement);
            });
        }
      });
    }

    if (hasChecked) {
      details.dispatchEvent(new Event("change", { bubbles: true }));
    }
  };

  const initBundleVariantSelectors = (scope = document) => {
    scope.querySelectorAll(selectors.bundleDetails).forEach(initBundleDetails);
  };

  // Fonction pour sauvegarder l'état d'un bundle
  const saveBundleState = (bundleContainer) => {
    const bundleDetails = bundleContainer.querySelector(
      selectors.bundleDetails,
    );
    if (!bundleDetails) return null;

    const state = {
      prices: new Map(),
      checkboxes: new Map(),
      slots: new Map(),
    };

    // Sauvegarder les prix/badges
    bundleDetails.querySelectorAll("[data-bundle-price]").forEach((priceEl) => {
      const productId = priceEl.dataset.productId;
      if (productId) {
        state.prices.set(productId, {
          innerHTML: priceEl.innerHTML,
          classList: Array.from(priceEl.classList),
          dataset: {
            currentPrice: priceEl.dataset.currentPrice || "",
            currentComparePrice: priceEl.dataset.currentComparePrice || "",
          },
        });
      }
    });

    // Sauvegarder les checkboxes
    bundleDetails.querySelectorAll(selectors.checkbox).forEach((checkbox) => {
      const productId = checkbox.dataset.productId;
      if (productId) {
        state.checkboxes.set(productId, {
          checked: checkbox.checked,
          value: checkbox.value,
          disabled: checkbox.disabled,
        });
      }
    });

    // Sauvegarder les slots
    bundleDetails.querySelectorAll(selectors.slot).forEach((slot) => {
      const productId = slot.dataset.productId;
      if (productId) {
        state.slots.set(productId, {
          active: slot.dataset.active || "false",
          height: slot.style.height || "",
          overflow: slot.style.overflow || "",
          margin: slot.style.margin || "",
          padding: slot.style.padding || "",
        });
      }
    });

    return state;
  };

  // Fonction pour restaurer l'état d'un bundle
  const restoreBundleState = (bundleContainer, state) => {
    const bundleDetails = bundleContainer.querySelector(
      selectors.bundleDetails,
    );
    if (!bundleDetails) return;

    // Si pas d'état sauvegardé, créer un état vide pour permettre l'application des règles
    const safeState = state || {
      prices: new Map(),
      checkboxes: new Map(),
      slots: new Map(),
    };

    // D'abord, restaurer les prix/badges pour les éléments sauvegardés
    safeState.prices.forEach((priceState, productId) => {
      const priceEl = bundleDetails.querySelector(
        `[data-bundle-price][data-product-id="${productId}"]`,
      );
      if (priceEl) {
        priceEl.innerHTML = priceState.innerHTML;
        priceEl.className = priceState.classList.join(" ");
        if (priceState.dataset.currentPrice) {
          priceEl.dataset.currentPrice = priceState.dataset.currentPrice;
        } else {
          delete priceEl.dataset.currentPrice;
        }
        if (priceState.dataset.currentComparePrice) {
          priceEl.dataset.currentComparePrice =
            priceState.dataset.currentComparePrice;
        } else {
          delete priceEl.dataset.currentComparePrice;
        }
      }
    });

    // Ensuite, s'assurer que tous les prix dans le nouveau HTML sont corrects
    // (au cas où il y aurait des prix qui n'étaient pas dans l'état sauvegardé)
    bundleDetails.querySelectorAll("[data-bundle-price]").forEach((priceEl) => {
      const productId = priceEl.dataset.productId;
      if (productId && !safeState.prices.has(productId)) {
        // Si ce prix n'était pas dans l'état sauvegardé, vérifier la checkbox
        const checkbox = bundleDetails.querySelector(
          `${selectors.checkbox}[data-product-id="${productId}"]`,
        );
        // Si la checkbox n'est pas cochée, réinitialiser le prix (enlever les badges)
        if (checkbox && !checkbox.checked) {
          resetPriceDisplays(bundleDetails, productId);
        }
      }
    });

    // Restaurer les checkboxes
    safeState.checkboxes.forEach((checkboxState, productId) => {
      const checkbox = bundleDetails.querySelector(
        `${selectors.checkbox}[data-product-id="${productId}"]`,
      );
      if (checkbox) {
        checkbox.checked = checkboxState.checked;
        checkbox.value = checkboxState.value;
        checkbox.disabled = checkboxState.disabled;
      }
    });

    // Restaurer les slots en vérifiant l'état de la checkbox
    // D'abord, restaurer les slots sauvegardés
    safeState.slots.forEach((slotState, productId) => {
      const slot = bundleDetails.querySelector(
        `${selectors.slot}[data-product-id="${productId}"]`,
      );
      if (slot) {
        const checkbox = bundleDetails.querySelector(
          `${selectors.checkbox}[data-product-id="${productId}"]`,
        );
        const shouldBeActive = checkbox ? checkbox.checked : false;

        // Si la checkbox n'est pas cochée, forcer le slot à rester fermé
        if (!shouldBeActive) {
          slot.dataset.active = "false";
          slot.style.height = "0";
          slot.style.overflow = "hidden";
          slot.style.margin = "0";
          slot.style.paddingInline = "0";
          slot.style.paddingBottom = "0";
          const restoredItem = slot.closest(selectors.item);
          if (isLastItem(restoredItem)) {
            const restoredInfo = restoredItem?.querySelector(selectors.info);
            if (restoredInfo) restoredInfo.style.paddingBottom = "0";
          }
        } else {
          // Sinon, restaurer l'état sauvegardé
          slot.dataset.active = slotState.active;
          slot.style.height = slotState.height;
          slot.style.overflow = slotState.overflow;
          slot.style.margin = slotState.margin;
          // Last item ouvert : restaurer le padding-bottom CSS sur info
          // pour conserver l'espace entre le label et le picker.
          const restoredItem = slot.closest(selectors.item);
          if (isLastItem(restoredItem)) {
            const restoredInfo = restoredItem?.querySelector(selectors.info);
            if (restoredInfo) restoredInfo.style.removeProperty("padding-bottom");
          }
        }
      }
    });

    // Ensuite, s'assurer que tous les slots dans le nouveau HTML sont corrects
    // (au cas où il y aurait des slots qui n'étaient pas dans l'état sauvegardé)
    bundleDetails.querySelectorAll(selectors.slot).forEach((slot) => {
      const productId = slot.dataset.productId;
      if (productId && !safeState.slots.has(productId)) {
        // Si ce slot n'était pas dans l'état sauvegardé, vérifier la checkbox
        const checkbox = bundleDetails.querySelector(
          `${selectors.checkbox}[data-product-id="${productId}"]`,
        );
        const shouldBeActive = checkbox ? checkbox.checked : false;

        // Si la checkbox n'est pas cochée, forcer le slot à rester fermé
        if (!shouldBeActive) {
          slot.dataset.active = "false";
          slot.style.height = "0";
          slot.style.overflow = "hidden";
          slot.style.margin = "0";
          slot.style.paddingInline = "0";
          slot.style.paddingBottom = "0";
          const unrestoredItem = slot.closest(selectors.item);
          if (isLastItem(unrestoredItem)) {
            const unrestoredInfo = unrestoredItem?.querySelector(selectors.info);
            if (unrestoredInfo) unrestoredInfo.style.paddingBottom = "0";
          }
        }
      }
    });

    // Enfin, appliquer les règles pour TOUS les éléments (même ceux qui étaient dans l'état sauvegardé)
    // Cela garantit la cohérence, surtout si l'état était null
    bundleDetails.querySelectorAll(selectors.checkbox).forEach((checkbox) => {
      const productId = checkbox.dataset.productId;
      if (!productId) return;

      if (!checkbox.checked) {
        // Si non cochée, forcer la fermeture du slot et réinitialiser le prix
        const slot = bundleDetails.querySelector(
          `${selectors.slot}[data-product-id="${productId}"]`,
        );
        if (slot) {
          slot.dataset.active = "false";
          slot.style.height = "0";
          slot.style.overflow = "hidden";
          slot.style.margin = "0";
          slot.style.paddingInline = "0";
          slot.style.paddingBottom = "0";
          const allItem = slot.closest(selectors.item);
          if (isLastItem(allItem)) {
            const allInfo = allItem?.querySelector(selectors.info);
            if (allInfo) allInfo.style.paddingBottom = "0";
          }
        }

        // Réinitialiser les prix pour enlever les badges
        resetPriceDisplays(bundleDetails, productId);
      }
    });

    // Réinitialiser les bindings en préservant l'état
    initBundleDetails(bundleDetails, true);

    // Rebinder tous les pickers existants
    const pickers = bundleDetails.querySelectorAll(selectors.picker);
    pickers.forEach((picker) => {
      const productId = picker.dataset.productId;
      if (productId) {
        const checkbox = bundleDetails.querySelector(
          `${selectors.checkbox}[data-product-id="${productId}"]`,
        );
        if (checkbox && checkbox.checked) {
          bindPicker(bundleDetails, picker);
        }
      }
    });
  };

  // Observer pour détecter et protéger les mises à jour du HTML ProductBundle
  const protectedContainers = new WeakSet();

  const protectBundleContainer = (bundleContainer) => {
    if (protectedContainers.has(bundleContainer)) return;
    protectedContainers.add(bundleContainer);

    // Intercepter le setter innerHTML pour sauvegarder l'état avant la mise à jour
    const originalInnerHTML = Object.getOwnPropertyDescriptor(
      Element.prototype,
      "innerHTML",
    );

    Object.defineProperty(bundleContainer, "innerHTML", {
      get() {
        return originalInnerHTML.get.call(this);
      },
      set() {
        // Le thème remplace ce bloc à chaque changement de variante du produit principal
        // (updateSourceFromDestination « ProductBundle »). Ce contenu est indépendant de la
        // variante sélectionnée : on ignore la mise à jour pour conserver prix, badges,
        // checkboxes, variantes choisies et réduction conditionnelle déjà affichés.
      },
      configurable: true,
      enumerable: true,
    });
  };

  const observeBundleContainer = (bundleContainer) => {
    if (protectedContainers.has(bundleContainer)) return;
    protectBundleContainer(bundleContainer);

    let isRestoring = false;
    let lastKnownState = saveBundleState(bundleContainer);
    let restoreTimeout = null;

    // Sauvegarder l'état très fréquemment pour avoir une sauvegarde récente
    const saveInterval = setInterval(() => {
      if (!isRestoring && bundleContainer.isConnected) {
        const currentState = saveBundleState(bundleContainer);
        if (currentState) {
          lastKnownState = currentState;
        }
      }
    }, 30); // Sauvegarde très fréquente (toutes les 30ms)

    const observer = new MutationObserver((mutations) => {
      // Ignorer les mutations pendant la restauration
      if (isRestoring) return;

      let shouldRestore = false;
      let stateToRestore = null;

      mutations.forEach((mutation) => {
        // Détecter les changements d'innerHTML (suppression/ajout de tous les enfants)
        if (
          mutation.type === "childList" &&
          mutation.target === bundleContainer
        ) {
          // Si beaucoup de nœuds sont supprimés/ajoutés, c'est probablement une mise à jour complète
          const removedCount = mutation.removedNodes.length;
          const addedCount = mutation.addedNodes.length;

          if (removedCount > 1 || addedCount > 1) {
            // Utiliser la dernière sauvegarde connue
            stateToRestore = lastKnownState;
            shouldRestore = true;
          }
        }
      });

      if (shouldRestore && stateToRestore) {
        // Annuler toute restauration en attente
        if (restoreTimeout) {
          clearTimeout(restoreTimeout);
        }

        isRestoring = true;

        // Restaurer immédiatement (dans le même tick si possible, sinon au prochain frame)
        restoreTimeout = setTimeout(() => {
          restoreBundleState(bundleContainer, stateToRestore);
          // Réinitialiser le flag après un court délai pour permettre d'autres mutations
          setTimeout(() => {
            isRestoring = false;
            restoreTimeout = null;
          }, 10);
        }, 0);
      }
    });

    observer.observe(bundleContainer, {
      childList: true,
      subtree: false,
      attributes: false,
    });

    // Stocker l'observer et l'interval pour nettoyage éventuel
    bundleContainer._bundleProtection = { observer, interval: saveInterval };
  };

  // Observer tous les containers ProductBundle existants et nouveaux
  const initBundleProtection = () => {
    document.querySelectorAll('[id^="ProductBundle-"]').forEach((container) => {
      observeBundleContainer(container);
    });
  };

  // Fonction pour appliquer les options par défaut aux bundles dans le modal
  // Applique les classes CSS par défaut si elles ne sont pas déjà présentes
  const applyBundleDefaultOptions = (bundleDetails) => {
    if (!bundleDetails) return;

    // Valeurs par défaut selon le schema (product_preview: true, price_below_title: true, show_mobile_media: true)
    const defaultOptions = {
      productPreview: true,
      priceBelowTitle: true,
      showMobileMedia: true,
    };

    // Appliquer les classes CSS si elles ne sont pas présentes
    if (
      defaultOptions.productPreview &&
      !bundleDetails.classList.contains("product-bundle--preview")
    ) {
      bundleDetails.classList.add("product-bundle--preview");
    }

    if (
      defaultOptions.priceBelowTitle &&
      !bundleDetails.classList.contains("product-bundle--price-below-title")
    ) {
      bundleDetails.classList.add("product-bundle--price-below-title");
    }

    if (
      defaultOptions.showMobileMedia &&
      !bundleDetails.classList.contains("product-bundle--show-mobile-media")
    ) {
      bundleDetails.classList.add("product-bundle--show-mobile-media");
    }
  };

  // Initialisation immédiate si le DOM est déjà chargé
  const initializeBundles = () => {
    initBundleVariantSelectors();
    initBundleProtection();

    // Initialiser aussi les bundles dans les modals (comme main-product-modal.liquid)
    // qui ont leur contenu directement dans le HTML
    document
      .querySelectorAll('.quick-view, .x-modal, [class*="modal"]')
      .forEach((modal) => {
        const bundles = modal.querySelectorAll(
          'details[is="product-bundle-details"]',
        );
        bundles.forEach((details) => {
          applyBundleDefaultOptions(details);
          initBundleDetails(details, false);
        });
        // Protéger les containers ProductBundle dans le modal
        modal
          .querySelectorAll('[id^="ProductBundle-"]')
          .forEach((container) => {
            observeBundleContainer(container);
          });
      });

    document.dispatchEvent(new CustomEvent("bundles:initialized", { bubbles: true }));
  };

  // Fonction pour initialiser les bundles dans un modal spécifique
  const initializeBundlesInModal = (modal, retryCount = 0) => {
    if (!modal) return;

    const maxRetries = 20;
    const retryDelay = 50;

    const bundles = modal.querySelectorAll(
      'details[is="product-bundle-details"]',
    );

    // Si pas de bundles trouvés et qu'on peut réessayer, attendre un peu
    if (bundles.length === 0 && retryCount < maxRetries) {
      setTimeout(() => {
        initializeBundlesInModal(modal, retryCount + 1);
      }, retryDelay);
      return;
    }

    // Vérifier que les scripts de données des variantes sont disponibles
    // Si ce n'est pas le cas, réessayer
    let hasVariantData = true;
    bundles.forEach((details) => {
      const checkboxes = details.querySelectorAll(selectors.checkbox);
      checkboxes.forEach((checkbox) => {
        const productId = checkbox.dataset.productId;
        if (productId) {
          // Vérifier si les données des variantes sont disponibles dans le modal
          const productInfo = modal.querySelector(
            `product-info[data-product-id="${productId}"]`,
          );
          if (productInfo) {
            const variantDataScript = productInfo.querySelector(
              "script[data-product-variant-data]",
            );
            if (!variantDataScript) {
              hasVariantData = false;
            }
          } else {
            // Si productInfo n'existe pas encore, peut-être que le contenu n'est pas chargé
            // Vérifier dans le modal directement
            const variantDataScript = modal.querySelector(
              `script[data-product-variant-data]`,
            );
            if (!variantDataScript) {
              hasVariantData = false;
            }
          }
        }
      });
    });

    // Si les données ne sont pas encore disponibles et qu'on peut réessayer
    if (!hasVariantData && retryCount < maxRetries) {
      setTimeout(() => {
        initializeBundlesInModal(modal, retryCount + 1);
      }, retryDelay);
      return;
    }

    bundles.forEach((details) => {
      applyBundleDefaultOptions(details);
      initBundleDetails(details, false);
    });

    // Protéger les containers ProductBundle dans le modal
    modal.querySelectorAll('[id^="ProductBundle-"]').forEach((container) => {
      observeBundleContainer(container);
    });

    modal.dispatchEvent(
      new CustomEvent("bundles:initialized", { bubbles: true, detail: { modal } }),
    );
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initializeBundles();
    });
  } else {
    // DOM déjà chargé, initialiser immédiatement
    initializeBundles();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initBundleVariantSelectors(event.target);
    // Protéger les nouveaux containers dans la section chargée
    event.target
      .querySelectorAll('[id^="ProductBundle-"]')
      .forEach((container) => {
        observeBundleContainer(container);
      });
  });

  // Observer les ajouts dynamiques de containers ProductBundle
  const globalObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          // Vérifier si le node lui-même est un container ProductBundle
          if (node.id && node.id.startsWith("ProductBundle-")) {
            observeBundleContainer(node);
            // Initialiser les bundles dans ce container
            const bundles = node.querySelectorAll(
              'details[is="product-bundle-details"]',
            );
            bundles.forEach((details) => {
              // Vérifier si c'est dans un modal
              const modal = details.closest(".quick-view, .x-modal");
              if (modal) {
                applyBundleDefaultOptions(details);
              }
              initBundleDetails(details, false);
            });
          }
          // Vérifier les containers ProductBundle dans le node
          node
            .querySelectorAll?.('[id^="ProductBundle-"]')
            .forEach((container) => {
              observeBundleContainer(container);
              // Initialiser les bundles dans ce container
              const bundles = container.querySelectorAll(
                'details[is="product-bundle-details"]',
              );
              bundles.forEach((details) => {
                // Vérifier si c'est dans un modal
                const modal = details.closest(".quick-view, .x-modal");
                if (modal) {
                  applyBundleDefaultOptions(details);
                }
                initBundleDetails(details, false);
              });
            });

          // Vérifier si des bundles sont ajoutés directement (dans les modals notamment)
          const bundles = node.querySelectorAll?.(
            'details[is="product-bundle-details"]',
          );
          if (bundles?.length) {
            bundles.forEach((details) => {
              // Vérifier si c'est dans un modal
              const modal = details.closest(".quick-view, .x-modal");
              if (modal) {
                applyBundleDefaultOptions(details);
              }
              initBundleDetails(details, false);
            });
            // Protéger les containers ProductBundle dans le node
            node
              .querySelectorAll?.('[id^="ProductBundle-"]')
              .forEach((container) => {
                observeBundleContainer(container);
              });
          }
        }
      });
    });
  });

  globalObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // Écouter l'événement quickview:loaded qui est dispatché après que le contenu soit chargé
  document.addEventListener("quickview:loaded", (event) => {
    // Trouver le modal ouvert
    const modal = document.querySelector(".quick-view[open], .x-modal[open]");
    if (modal) {
      // Initialiser les bundles après que le contenu soit chargé
      // Utiliser requestAnimationFrame pour s'assurer que le DOM est prêt
      requestAnimationFrame(() => {
        initializeBundlesInModal(modal);
      });
    }
  });

  // Écouter l'ouverture du modal pour initialiser les bundles immédiatement
  // (pour les modals qui ont déjà leur contenu dans le HTML)
  document.addEventListener("quickview:open", (event) => {
    // Trouver le modal ouvert
    const modal = document.querySelector(".quick-view[open], .x-modal[open]");
    if (modal) {
      // Vérifier si le contenu est déjà chargé (pour les modals statiques)
      const content = modal.querySelector(
        ".quick-view__content, .drawer__content",
      );
      if (content && content.innerHTML.trim().length > 0) {
        // Le contenu est déjà là, initialiser immédiatement
        requestAnimationFrame(() => {
          initializeBundlesInModal(modal);
        });
      }
      // Sinon, attendre l'événement quickview:loaded
    }
  });

  // Écouter aussi l'événement modal:afterShow pour être sûr
  document.addEventListener("modal:afterShow", (event) => {
    const modal = event.target;
    if (
      modal &&
      (modal.classList.contains("quick-view") ||
        modal.classList.contains("x-modal"))
    ) {
      // Vérifier si le contenu est déjà chargé
      const content = modal.querySelector(
        ".quick-view__content, .drawer__content",
      );
      if (content && content.innerHTML.trim().length > 0) {
        // Le contenu est déjà là, initialiser immédiatement
        requestAnimationFrame(() => {
          initializeBundlesInModal(modal);
        });
      }
    }
  });

  // Observer les changements d'attribut 'open' sur les modals pour détecter l'ouverture
  const modalObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === "attributes" && mutation.attributeName === "open") {
        const modal = mutation.target;
        if (
          modal &&
          modal.hasAttribute("open") &&
          (modal.classList.contains("quick-view") ||
            modal.classList.contains("x-modal"))
        ) {
          // Vérifier si le contenu est déjà chargé
          const content = modal.querySelector(
            ".quick-view__content, .drawer__content",
          );
          if (content && content.innerHTML.trim().length > 0) {
            // Le contenu est déjà là, initialiser immédiatement
            requestAnimationFrame(() => {
              initializeBundlesInModal(modal);
            });
          }
          // Sinon, attendre l'événement quickview:loaded
        }
      }
    });
  });

  // Observer tous les modals existants et nouveaux
  const observeModals = () => {
    document.querySelectorAll(".quick-view, .x-modal").forEach((modal) => {
      modalObserver.observe(modal, {
        attributes: true,
        attributeFilter: ["open"],
      });
    });
  };

  // Initialiser l'observation des modals
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      observeModals();
    });
  } else {
    observeModals();
  }

  // Observer aussi les nouveaux modals ajoutés au DOM
  const modalGlobalObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          if (
            node.classList?.contains("quick-view") ||
            node.classList?.contains("x-modal")
          ) {
            modalObserver.observe(node, {
              attributes: true,
              attributeFilter: ["open"],
            });
          }
          // Vérifier aussi les modals dans les nodes ajoutés
          node.querySelectorAll?.(".quick-view, .x-modal").forEach((modal) => {
            modalObserver.observe(modal, {
              attributes: true,
              attributeFilter: ["open"],
            });
          });
        }
      });
    });
  });

  modalGlobalObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // Observer les changements dans le contenu des modals pour détecter quand le contenu est injecté
  const modalContentObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
        // Vérifier si des bundles ont été ajoutés
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Vérifier si le node contient des bundles
            const bundles = node.querySelectorAll?.(
              'details[is="product-bundle-details"]',
            );
            if (bundles && bundles.length > 0) {
              // Trouver le modal parent
              const modal = node.closest(".quick-view, .x-modal");
              if (modal && modal.hasAttribute("open")) {
                // Initialiser les bundles après un court délai pour s'assurer que tout est prêt
                requestAnimationFrame(() => {
                  initializeBundlesInModal(modal);
                });
              }
            }

            // Vérifier si le node lui-même est un bundle
            if (
              node.matches &&
              node.matches('details[is="product-bundle-details"]')
            ) {
              const modal = node.closest(".quick-view, .x-modal");
              if (modal && modal.hasAttribute("open")) {
                requestAnimationFrame(() => {
                  applyBundleDefaultOptions(node);
                  initBundleDetails(node, false);
                });
              }
            }
          }
        });
      }
    });
  });

  // Observer le contenu des modals pour détecter les changements
  const observeModalContent = () => {
    document
      .querySelectorAll(".quick-view__content, .drawer__content")
      .forEach((content) => {
        modalContentObserver.observe(content, {
          childList: true,
          subtree: true,
        });
      });
  };

  // Observer aussi les nouveaux modals ajoutés
  const modalContentGlobalObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          // Vérifier si le node est un modal ou contient un modal
          if (
            node.classList?.contains("quick-view") ||
            node.classList?.contains("x-modal")
          ) {
            const content = node.querySelector(
              ".quick-view__content, .drawer__content",
            );
            if (content) {
              modalContentObserver.observe(content, {
                childList: true,
                subtree: true,
              });
            }
          }
          // Vérifier les modals dans le node
          node
            .querySelectorAll?.(".quick-view__content, .drawer__content")
            .forEach((content) => {
              modalContentObserver.observe(content, {
                childList: true,
                subtree: true,
              });
            });
        }
      });
    });
  });

  // Initialiser l'observation du contenu des modals
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      observeModalContent();
      modalContentGlobalObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });
  } else {
    observeModalContent();
    modalContentGlobalObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
})();
